import { fetchPageContent, fetchTemplateAnnotations, getMgnlContext } from './services/magnolia-service.js';

import styles from './magnolia-content.css';
import { defineElement, html, TemplateElement } from '@webtides/element-js';
import mgnlConfig from './magnolia.config.js';
import './mgnl/mgnl-editable-page.js';
import '../../../frontend/src/components/spinner/spinner.js';

class MagnoliaContent extends TemplateElement {
    properties() {
        return {
            mgnlBaseUrl: '/',
            mgnlContextPath: '/',
            availableLanguages: ['de', 'it', 'fr'],
            language: 'de',
            appBase: '/',

            mgnlApiPages: '/.rest/delivery/pages/eb/v1',
            mgnlApiAnnotations: '/.rest/template-annotations/v1',
            environment: {},
            content: null,
            error: null,
            templateAnnotation: null,
            addPageAnnotation: true,
            isLoading: false,
        };
    }

    constructor() {
        super({ adoptGlobalStyles: false, styles: [styles] });
        this._options.shadowRender =
            !this.hasAttribute('edit-mode') && globalThis?.magnoliaContext?.isMagnoliaEdit !== true;
    }

    watch() {
        return {
            mgnlContentPath: async () => {
                await this.loadContent();
            },
        };
    }

    events() {
        return {
            this: {
                ['magnolia-content-click']: (e) => {
                    const { internalLinkPath } = e.detail;
                    if (internalLinkPath) {
                        // internal link / trigger refetch of content
                        e.preventDefault();
                        this.mgnlContext = getMgnlContext(
                            internalLinkPath,
                            this.appBase,
                            this.availableLanguages,
                            this.language,
                        );
                        this.mgnlContentPath = internalLinkPath;
                    }
                },
            },
        };
    }

    async connected() {
        try {
            this.environment = {
                appBase: this.appBase,
                mgnlBaseUrl: this.mgnlBaseUrl,
                mgnlContextPath: this.mgnlContextPath,
                languages: this.availableLanguages,
            };

            this.mgnlContext = getMgnlContext(
                this.mgnlContentPath,
                this.appBase,
                this.availableLanguages,
                this.language,
            );
        } catch (e) {
            console.error(e);
            this.dispatch('error');
        }

        await this.loadContent();
    }

    async loadContent() {
        this.isLoading = true;
        this.error = null;
        try {
            const content = await fetchPageContent(
                this.mgnlContext,
                `${this.mgnlBaseUrl}${this.mgnlContextPath}${this.mgnlApiPages}`,
            );

            if (content.hasOwnProperty('error')) {
                this.error = content.error;
                throw new Error(`${content.error.code} : ${content.error.message}`);
            }
            const annotations = await fetchTemplateAnnotations(
                this.mgnlContext,
                `${this.mgnlBaseUrl}${this.mgnlContextPath}${this.mgnlApiAnnotations}`,
            );

            this.content = content;
            this.templateAnnotations = annotations;
            // load event
            this.dispatch('load', { title: this.content?.title, subtitle: this.content?.subtitle });
        } catch (e) {
            // error event
            this.dispatch('error', e);
            this.error = e.message;
        }
        this.isLoading = false;
    }

    templateContent() {
        if (this.error) {
            return html`<div class="container flex flex-col items-center">
                <slot name="error">
                    ${this.error?.code
                        ? html`<h1>Error: ${this.error.code}</h1>
                              <p>${this.error.message}</p>`
                        : html`<p>${this.error}</p>`}
                </slot>
            </div>`;
        }

        if (!this.content && this.isLoading) {
            return html`<div class="container flex flex-col items-center">
                <slot name="loading">
                    <mb-spinner></mb-spinner>
                </slot>
            </div>`;
        }

        return html`<mgnl-editable-page
            .content="${this.content}"
            .templateAnnotations="${this.templateAnnotations}"
            .config="${mgnlConfig}"
            .mgnlContext="${this.mgnlContext}"
            .environment="${this.environment}"
            .addPageAnnotation="${this.addPageAnnotation}"
        ></mgnl-editable-page>`;
    }

    template() {
        return html`<div class="magnolia-content tailwind-container">${this.templateContent()}</div>`;
    }
}

defineElement('magnolia-content', MagnoliaContent);
